import * as React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { useDispatch, useSelector } from 'react-redux';
import {
	Box,
	Modal,
	Switch,
	Typography,
	TextField,
	Button,
	IconButton,
	Grid,
	FormControlLabel,
	Select,
	MenuItem,
	InputLabel,
	FormControl,
} from '@mui/material';
import {
	deltebannerDispatch,
	// updatePackageDispatch,
} from 'reducers/HomeReducer';
import BasicModal from 'app/components/BasicModal';
import { setUPC, toggleUProductCModal } from 'reducers/UiReducer';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { deletePDispatch, updatePDispatch } from 'reducers/GeneralReducer';

/* ══════════════════════════════════════════════════════════════
   EDIT PACKAGE MODAL — title, tag, description, price, rating,
   package category, status, thumbnail
══════════════════════════════════════════════════════════════ */
const EditPackageModal = ({
	open,
	onClose,
	row,
	loginInfo,
	navigate,
	categoryOptions,
}) => {
	const dispatch = useDispatch();
	const [form, setForm] = React.useState({
		title: '',
		tag: '',
		desc: '',
		ad_price: '',
		rating: '',
		package_categories_id: '',
		status: '1',
	});
	const [submitting, setSubmitting] = React.useState(false);
	const [imgFile, setImgFile] = React.useState(null);
	const [imgPreview, setImgPreview] = React.useState('');
	const fileInputRef = React.useRef(null);

	React.useEffect(() => {
		if (open && row) {
			setForm({
				title: row.title || '',
				tag: row.tag || '',
				desc: row.desc || '',
				ad_price: row.ad_price ?? '',
				rating: row.rating ?? '',
				package_categories_id: row.package_categories_id ?? '',
				status: String(row.status) === '1' ? '1' : '0',
			});
			setImgFile(null);
			setImgPreview(row.imgpath || '');
		}
	}, [open, row]);

	// Revoke object URL when replaced/unmounted to avoid memory leaks
	React.useEffect(() => {
		return () => {
			if (imgFile && imgPreview) URL.revokeObjectURL(imgPreview);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [imgFile]);

	const handleChange = (field) => (e) =>
		setForm((p) => ({ ...p, [field]: e.target.value }));

	const handleStatusChange = (e) =>
		setForm((p) => ({ ...p, status: e.target.checked ? '1' : '0' }));

	const handleImageSelect = (e) => {
		const file = e.target.files?.[0];
		if (!file) return;
		if (!file.type.startsWith('image/')) {
			Swal.fire({
				title: 'Invalid file',
				text: 'Please select an image file for the thumbnail.',
				icon: 'error',
			});
			return;
		}
		setImgFile(file);
		setImgPreview(URL.createObjectURL(file));
	};

	const handleRemoveNewImage = () => {
		setImgFile(null);
		setImgPreview(row?.imgpath || '');
		if (fileInputRef.current) fileInputRef.current.value = '';
	};

	const handleSubmit = () => {
		if (!form.title?.trim()) {
			Swal.fire({
				title: 'Title required',
				text: 'Please enter the package title.',
				icon: 'error',
			});
			return;
		}
		if (
			form.ad_price !== '' &&
			(isNaN(Number(form.ad_price)) || Number(form.ad_price) < 0)
		) {
			Swal.fire({
				title: 'Invalid price',
				text: 'Please enter a valid price.',
				icon: 'error',
			});
			return;
		}
		if (
			form.rating !== '' &&
			(isNaN(Number(form.rating)) ||
				Number(form.rating) < 0 ||
				Number(form.rating) > 5)
		) {
			Swal.fire({
				title: 'Invalid rating',
				text: 'Rating should be a number between 0 and 5.',
				icon: 'error',
			});
			return;
		}

		const fields = {
			title: form.title,
			tag: form.tag,
			desc: form.desc,
			ad_price: form.ad_price,
			rating: form.rating,
			package_categories_id: form.package_categories_id,
			status: form.status,
		};

		// Use multipart FormData when a new thumbnail is picked so the file
		// actually reaches the backend; otherwise send the plain object.
		let payload;
		if (imgFile) {
			payload = new FormData();
			Object.entries(fields).forEach(([key, value]) =>
				payload.append(key, value ?? ''),
			);
			payload.append('pimage', imgFile);
		} else {
			payload = fields;
		}

		setSubmitting(true);
		dispatch(
			updatePDispatch(payload, navigate, row?.id, Swal, loginInfo?.token),
		);
		setSubmitting(false);
		onClose();
	};

	return (
		<Modal open={open} onClose={onClose}>
			<Box
				sx={{
					position: 'absolute',
					top: '50%',
					left: '50%',
					transform: 'translate(-50%, -50%)',
					bgcolor: 'background.paper',
					boxShadow: 24,
					borderRadius: '10px',
					width: { xs: '92vw', sm: 560 },
					maxHeight: '88vh',
					overflowY: 'auto',
					p: 4,
				}}>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'flex-start',
						justifyContent: 'space-between',
						mb: 2,
					}}>
					<Box>
						<Typography variant='h6' sx={{ fontWeight: 700 }}>
							Edit Package
						</Typography>
						{row?.title && (
							<Typography variant='body2' color='text.secondary'>
								{row.title}
							</Typography>
						)}
					</Box>
					<IconButton onClick={onClose} size='small'>
						<i className='ri-close-line' style={{ fontSize: 18 }} />
					</IconButton>
				</Box>

				<Grid container spacing={2}>
					<Grid item xs={12} sm={6}>
						<TextField
							label='Title'
							variant='outlined'
							size='small'
							fullWidth
							value={form.title}
							onChange={handleChange('title')}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextField
							label='Tag'
							variant='outlined'
							size='small'
							fullWidth
							value={form.tag}
							onChange={handleChange('tag')}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextField
							label='Price'
							variant='outlined'
							size='small'
							fullWidth
							type='number'
							value={form.ad_price}
							onChange={handleChange('ad_price')}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextField
							label='Rating'
							variant='outlined'
							size='small'
							fullWidth
							type='number'
							inputProps={{ min: 0, max: 5, step: 0.1 }}
							value={form.rating}
							onChange={handleChange('rating')}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<FormControl size='small' fullWidth>
							<InputLabel id='pkg-category-label'>Package Category</InputLabel>
							<Select
								labelId='pkg-category-label'
								label='Package Category'
								value={form.package_categories_id}
								onChange={handleChange('package_categories_id')}
								displayEmpty
								disabled={!categoryOptions || categoryOptions.length === 0}>
								{(!categoryOptions || categoryOptions.length === 0) && (
									<MenuItem value=''>
										<em>No categories available</em>
									</MenuItem>
								)}
								{categoryOptions?.map((c) => (
									<MenuItem key={c.id} value={c.id}>
										{c.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
					<Grid
						item
						xs={12}
						sm={6}
						sx={{ display: 'flex', alignItems: 'center' }}>
						<FormControlLabel
							control={
								<Switch
									checked={form.status === '1'}
									onChange={handleStatusChange}
									color='primary'
								/>
							}
							label={form.status === '1' ? 'Active' : 'Inactive'}
						/>
					</Grid>
					<Grid item xs={12}>
						<TextField
							label='Description'
							variant='outlined'
							size='small'
							fullWidth
							multiline
							rows={3}
							value={form.desc}
							onChange={handleChange('desc')}
						/>
					</Grid>
				</Grid>

				{/* Thumbnail image */}
				<Box sx={{ mt: 2.5 }}>
					<Typography
						variant='caption'
						sx={{
							color: 'text.secondary',
							fontWeight: 600,
							display: 'block',
							mb: 1,
						}}>
						THUMBNAIL
					</Typography>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
						<Box
							sx={{
								width: 72,
								height: 72,
								borderRadius: '8px',
								border: '1px solid rgba(0,0,0,0.15)',
								overflow: 'hidden',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								flexShrink: 0,
								bgcolor: '#fafafa',
							}}>
							{imgPreview ? (
								<img
									src={imgPreview}
									alt='Thumbnail preview'
									style={{ width: '100%', height: '100%', objectFit: 'cover' }}
								/>
							) : (
								<Typography variant='caption' color='text.secondary'>
									No image
								</Typography>
							)}
						</Box>
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
							<Button
								variant='outlined'
								size='small'
								component='label'
								startIcon={<i className='ri-upload-2-line' />}>
								{imgFile ? 'Choose Different Image' : 'Change Thumbnail'}
								<input
									ref={fileInputRef}
									type='file'
									accept='image/*'
									hidden
									onChange={handleImageSelect}
								/>
							</Button>
							{imgFile && (
								<Button
									variant='text'
									size='small'
									color='inherit'
									onClick={handleRemoveNewImage}>
									Revert to current image
								</Button>
							)}
						</Box>
					</Box>
				</Box>

				<Box
					sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 3 }}>
					<Button onClick={onClose} variant='outlined' size='small'>
						Cancel
					</Button>
					<Button
						onClick={handleSubmit}
						disabled={submitting}
						variant='contained'
						size='small'>
						Save Changes
					</Button>
				</Box>
			</Box>
		</Modal>
	);
};

export default function ProductTable() {
	const { loginInfo, category, banner, packages } = useSelector(
		(state) => state.home,
	);
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [open, setOpen] = React.useState(false);
	const [selectedImage, setSelectedImage] = React.useState('');
	const [editModal, setEditModal] = React.useState({
		open: false,
		row: null,
	});

	const handleOpen = (imgpath) => {
		setSelectedImage(imgpath);
		setOpen(true);
	};

	const handleClose = () => {
		setOpen(false);
		setSelectedImage('');
	};
	const handleStatusToggle = (row) => {
		const newStatus = String(row.status) === '1' ? '0' : '1';
		// dispatch your update action here, e.g.:
		// dispatch(updateBannerStatus(row.id, newStatus, loginInfo?.token));
		console.log('Toggle banner', row.id, 'to status', newStatus);
	};

	const openEdit = (row) => setEditModal({ open: true, row });
	const closeEdit = () => setEditModal({ open: false, row: null });

	const columns = [
		{
			field: 'id',
			headerName: 'ID',
			width: 150,
		},
		{
			field: 'title',
			headerName: 'Title',
			width: 200,
		},
		{
			field: 'tag',
			headerName: 'Tag',
			width: 300,
		},
		{
			field: 'desc',
			headerName: 'Descriptions',
			width: 300,
		},
		{
			field: 'ad_price',
			headerName: 'Price',
			width: 300,
		},
		{
			field: 'rating',
			headerName: 'Rating',
			width: 300,
		},
		{
			field: 'package_categories_id',
			headerName: 'Package Category',
			width: 300,
		},
		{
			field: 'status',
			headerName: 'Status',
			width: 120,
			renderCell: (cellValues) => {
				const isOn = String(cellValues.row.status) === '1';
				return (
					<Switch
						checked={isOn}
						onChange={() => handleStatusToggle(cellValues.row)}
						color='primary'
					/>
				);
			},
		},

		{
			field: 'imgpath',
			headerName: 'Thumbnail',
			width: 300,
			renderCell: (cellValues) => {
				return (
					<Box style={{ display: 'flex', justifyContent: 'center' }}>
						<img
							src={cellValues.row.imgpath}
							alt='Thumbnail'
							style={{
								height: 50,
								width: 50,
								cursor: 'pointer',
								borderRadius: '5px',
							}}
							onClick={() => handleOpen(cellValues.row.imgpath)}
						/>
					</Box>
				);
			},
		},
		{
			field: 'created_at',
			headerName: 'Created At',
			width: 200,
			renderCell: (params) => new Date(params.value).toLocaleString(),
		},
		{
			field: 'updated_at',
			headerName: 'Updated At',
			width: 200,
			renderCell: (params) => new Date(params.value).toLocaleString(),
		},
		{
			field: 'action',
			headerName: 'Action',
			width: 300,
			renderCell: (cellValues) => {
				return (
					<Box style={{ display: 'flex', justifyContent: 'space-between' }}>
						<a
							onClick={() => openEdit(cellValues.row)}
							href='javascript:void(0);'
							className='text-info fs-14 lh-1'
							style={{ marginRight: 10 }}>
							<i className='ri-edit-line'></i>
						</a>
						<a
							onClick={() => {
								Swal.fire({
									title: 'Are you sure?',
									text: "You won't be able to revert this!",
									icon: 'warning',
									showCancelButton: true,
									confirmButtonColor: '#3085d6',
									cancelButtonColor: '#d33',
									confirmButtonText: 'Yes, delete it!',
								}).then((result) => {
									if (result.isConfirmed) {
										dispatch(
											deletePDispatch(cellValues.id, Swal, loginInfo?.token),
										);
									}
								});
							}}
							href='javascript:void(0);'
							className='text-danger fs-14 lh-1'>
							<i className='ri-delete-bin-5-line'></i>
						</a>
					</Box>
				);
			},
		},
	];

	return (
		<Box sx={{ height: 400, width: '100%' }}>
			{packages && packages?.data?.length > 0 ? (
				<DataGrid
					sx={{
						'&.MuiDataGrid-root .MuiDataGrid-cell:focus': {
							outline: 'none',
						},
						'&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus': {
							outline: 'none',
						},
						'& .MuiDataGrid-virtualScroller': {
							'&::-webkit-scrollbar': {
								height: '8px',
								backgroundColor: '#f5f5f5',
							},
							'&::-webkit-scrollbar-thumb': {
								backgroundColor: '#888',
								borderRadius: '4px',
							},
							'&::-webkit-scrollbar-thumb:hover': {
								backgroundColor: '#555',
							},
						},
					}}
					rows={packages?.data}
					columns={columns}
					initialState={{
						pagination: {
							paginationModel: {
								pageSize: 5,
							},
						},
					}}
					pageSizeOptions={[5]}
					disableRowSelectionOnClick
				/>
			) : (
				<p>No Record Found</p>
			)}

			{/* Image Modal */}
			<Modal open={open} onClose={handleClose}>
				<Box
					sx={{
						position: 'absolute',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						bgcolor: 'background.paper',
						boxShadow: 24,
						p: 4,
						borderRadius: '10px',
					}}>
					<img
						src={selectedImage}
						alt='Expanded Thumbnail'
						style={{
							maxHeight: '80vh',
							maxWidth: '80vw',
							borderRadius: '10px',
						}}
					/>
				</Box>
			</Modal>

			{/* Edit Package Modal */}
			<EditPackageModal
				open={editModal.open}
				onClose={closeEdit}
				row={editModal.row}
				loginInfo={loginInfo}
				navigate={navigate}
				categoryOptions={category?.data}
			/>

			<BasicModal />
		</Box>
	);
}
